import { useEffect } from "react"
import { usePageContext } from "vike-react/usePageContext"
import useAsync from "./useAsync"
import useSiteLanguage, { useCurrency } from "./useSiteLanguage"
import { LoadProduct, PRODUCT_FIELDS } from "../api"
import { apiPid, samePid, langFromPath } from "../utils/contentRoutes"
import { isSignedIn } from "../storeApi"
import { productPidFromPath } from "../pages/productSSR"
import { trackViewItem } from "../tracking"
import { track } from "../analytics"
import { noteViewed } from "../store/recentlyViewed"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import { onPurchaseComplete } from "../checkout/purchase"

// the product last counted as viewed, and when: a page that mounts the hook
// more than once (or re-renders it) counts one view, a visit later another
const VIEW_GAP_MS = 30 * 1000
let lastView = { pid: null, at: 0 }
const firstViewOf = (pid) => {
  const now = Date.now()
  if (lastView.pid === pid && now - lastView.at < VIEW_GAP_MS) return false
  lastView = { pid, at: now }
  return true
}

// what only the reader's own call can answer: whether they own it, and the
// stock at this moment (the rest of the product is what the server sent)
const READER_FIELDS = "pid is_bought expired stock in_stock availableForPurchase"

/**
 * Product for the current /book|/album|/product URL. Seeds from the
 * server-loaded pageContext.storefrontProduct, refetches client-side when
 * the pid or language changes, and fires Tracking_ViewItem once per pid.
 */
export default function useProduct() {
  const pageContext = usePageContext()
  const language = useSiteLanguage()
  const currency = useCurrency()
  const { route, pid, sub } = productPidFromPath(pageContext.urlPathname)
  const fullPid = apiPid(pid)
  const ssr = pageContext.storefrontProduct
  const initial = samePid(ssr?.pid, fullPid) ? ssr : null
  // the server's product is the whole answer while it is in the language and
  // currency the reader sees (pages/productSSR asks in the URL's language and
  // the store's currency): the browser then asks only what depends on who reads
  const seedFits = !!initial && language === (langFromPath(pageContext.urlPathname || "/") || "en") && currency === pageContext.storeCurrency?.currency?.toLowerCase()

  const load = () => {
    if (!seedFits) return LoadProduct({ pid: fullPid, language, currency, addToHistory: true, fields: PRODUCT_FIELDS })
    // a guest owns nothing and has no history: the server's answer stands
    if (!isSignedIn()) return Promise.resolve(initial)
    // only what was asked for is taken over: the server's product stays the rest of the answer
    return LoadProduct({ pid: fullPid, language, currency, addToHistory: true, fields: READER_FIELDS }).then((own) => {
      if (!own?.pid || own.error) return initial
      const mine = Object.fromEntries(READER_FIELDS.split(" ").filter((key) => own[key] !== undefined).map((key) => [key, own[key]]))
      return { ...initial, ...mine }
    })
  }

  const { result, pending, loaded, refresh } = useAsync(load, [fullPid, language, currency, seedFits], { initial, enabled: !!pid })

  // The server knows the product but not the reader, so `is_bought` is false
  // in what it sends; the browser's own call is the one that knows. Until it
  // answers, nothing about ownership is settled.
  const fresh = loaded && samePid(result?.pid, fullPid)
  const product = samePid(result?.pid, fullPid) ? result : initial

  // the api refused the session the first call carried (the device was
  // signed out elsewhere): the reader is a guest now, and the product is
  // asked for again as one — the page settles on "Buy", not on waiting
  useEffect(() => {
    if (typeof window === "undefined") return
    const onRefused = () => refresh()
    window.addEventListener(PlatformAuth.SESSION_REFUSED_EVENT, onRefused)
    return () => window.removeEventListener(PlatformAuth.SESSION_REFUSED_EVENT, onRefused)
  }, [refresh])

  // a purchase settled (checkout/purchase): if this product was in it, ask
  // for it again — its is_bought flips and the page shows Open in place of
  // Buy without a reload, whether or not the thank-you screen is still up
  useEffect(() => onPurchaseComplete(({ pids }) => {
    if (!fullPid) return
    if (pids?.length && !pids.some((p) => samePid(p, fullPid))) return
    refresh()
  }), [fullPid, refresh])

  useEffect(() => {
    if (!product?.pid) return
    // several parts of a page read the product (the page, its panels): the
    // view is one — "Most viewed" counts these (aldoo-com-api catalog/lib/product-counters)
    if (!firstViewOf(product.pid)) return
    trackViewItem(product)
    track("PRODUCT_VIEW", { pid: product.pid })
    noteViewed(product.pid)
  }, [product?.pid])

  return { route, pid, sub, product, fresh, pending: pending && !product, refresh }
}
